import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { getMe } from '@/api/auth'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { LoginPage } from '@/pages/LoginPage'
import { CandidateLayout } from '@/pages/candidate/CandidateLayout'
import { LanguageScreen } from '@/pages/candidate/LanguageScreen'
import { AvatarScreen } from '@/pages/candidate/AvatarScreen'
import { InstructionsScreen } from '@/pages/candidate/InstructionsScreen'
import { InterviewScreen } from '@/pages/candidate/InterviewScreen'
import { SummaryScreen } from '@/pages/candidate/SummaryScreen'
import { ThankYouScreen } from '@/pages/candidate/ThankYouScreen'
import { CompanyPage } from '@/pages/CompanyPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { SessionDetailPage } from '@/pages/SessionDetailPage'
import { AdminRoute } from '@/components/layout/AdminRoute'
import { JoinLayout } from '@/pages/join/JoinLayout'
import { PreferencesScreen as JoinPreferencesScreen } from '@/pages/join/PreferencesScreen'
import { JoinResolver } from '@/pages/join/JoinResolver'
import { StatusScreen as JoinStatusScreen } from '@/pages/join/StatusScreen'
import { DeploymentConfigProvider } from '@/context/DeploymentConfigContext'

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { tokens, setAuth, clearAuth, setLoading } = useAuthStore()

  useEffect(() => {
    if (!tokens?.access) {
      setLoading(false)
      return
    }

    getMe(tokens.access)
      .then((user) => setAuth(user, tokens))
      .catch(() => clearAuth())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/" element={<Navigate to="/candidate/language" replace />} />

          <Route
            path="/candidate"
            element={
              <DeploymentConfigProvider>
                <CandidateLayout />
              </DeploymentConfigProvider>
            }
          >
            <Route index element={<Navigate to="language" replace />} />
            <Route path="language" element={<LanguageScreen />} />
            <Route path="avatar" element={<AvatarScreen />} />
            <Route path="instructions" element={<InstructionsScreen />} />
            <Route path="interview" element={<InterviewScreen />} />
            <Route path="summary" element={<SummaryScreen />} />
            <Route path="thankyou" element={<ThankYouScreen />} />
          </Route>

          <Route
            path="/company"
            element={
              <ProtectedRoute>
                <CompanyPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AdminRoute />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="session/:id" element={<SessionDetailPage />} />
          </Route>

          <Route path="/j/:sessionId" element={<JoinLayout />}>
            <Route index element={<JoinResolver />} />
            <Route path="preferences" element={<JoinPreferencesScreen />} />
            <Route path="status" element={<JoinStatusScreen />} />
            <Route path="setup" element={<InstructionsScreen />} />
            <Route path="interview" element={<InterviewScreen />} />
            <Route path="thankyou" element={<ThankYouScreen />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}